import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Container, Paper, Button, Link } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '../../theme/theme';
import { COLORS } from '../../theme/themeConstants';
import AuthLayout from './AuthLayout';
import { useSetNewPassword } from '../Query/SetNewPasswordQuery';
import AllForm from './Form';
import * as Yup from 'yup';
import ConfirmationDialog from './ConfirmationDialog';

// NOTE: Authentication pages intentionally kept in English regardless of language setting
// This ensures consistency and avoids confusion during authentication process

// import { useSetNewPassword } from '../Query/SetNewPasswordQuery'; // Uncomment and implement as needed

const SetNewPassword: React.FC = () => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // const email = new URLSearchParams(location.search).get('email');
  // const phone = new URLSearchParams(location.search).get('phone');
  // const channel = new URLSearchParams(location.search).get('channel');
  
  // Extract and properly decode URL parameters, especially phone with + sign
  const searchParams = new URLSearchParams(location.search);
  const rawEmail = searchParams.get('email');
  const rawPhone = searchParams.get('phone');
  const email = rawEmail ? decodeURIComponent(rawEmail) : null;
  const phone = rawPhone ? decodeURIComponent(rawPhone) : null;
  const channel = searchParams.get('channel');

  const { mutate: setNewPasswordMutation, isPending: setNewPasswordLoading } =
    useSetNewPassword();

  const previousHash = useRef(window.location.hash);
  const newHash = useRef<string | null>(null);

  useEffect(() => {
    const handleHashChange = (event: HashChangeEvent) => {
      event.preventDefault();
      newHash.current = window.location.hash;
      window.location.hash = previousHash.current;
      setShowConfirmDialog(true);
      setPendingNavigation(false);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const initialValues = {
    password: '',
    confirmPassword: '',
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters'),
    confirmPassword: Yup.string()
      .required('Confirm Password is required')
      .oneOf([Yup.ref('password')], 'Passwords must match'),
  });

  const fields = [
    {
      name: 'password',
      label: 'New Password',
      type: 'password',
      isRequired: true,
    },
    {
      name: 'confirmPassword',
      label: 'Confirm new password',
      type: 'password',
      isRequired: true,
    },
  ];

  const onSubmit = async (values: Record<string, any>) => {
    const emailOrPhone = channel === 'email' ? { email: email } : { phoneNumber: phone }

    const modifiedValues = {
      ...emailOrPhone,
            newPassword: values.password,
            channel: channel,
                  platformType : "merchant"
    };

    try {
      await setNewPasswordMutation(modifiedValues, {
        onSuccess: () => {
          navigate('/');
        },
        onError: (error: any) => {
          setError(error.message || 'An error occurred during password reset.');
        },
      });
    } catch (error: any) {
      setError(error.message || 'An unexpected error occurred.');
    }
  };

  const SetNewPasswordForm = AllForm({
    initialValues,
    validationSchema,
    onSubmit,
    fields,
    customButton: (formikProps: any) => (
      <Button
        type="submit"
        variant="contained"
        sx={{
          backgroundColor: COLORS.primary.main,
          color: '#fff',
          fontWeight: 600,
          fontSize: 16,
          borderRadius: 2,
          mb: 2,
          py: 1.5,
          px: 3, // Horizontal padding
          whiteSpace: 'nowrap', // Prevent text wrapping
        }}
        disabled={formikProps.isSubmitting}
      >
        {setNewPasswordLoading ? 'Submitting...' : 'Reset password'}
      </Button>
    ),
  });

  const handleBackToLogin = () => {
    setShowConfirmDialog(true);
    setPendingNavigation(true);
  };

  const handleConfirmNavigation = () => {
    setShowConfirmDialog(false);
    if (pendingNavigation && newHash.current) {
      window.location.hash = '#/';
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleCancelNavigation = () => {
    setShowConfirmDialog(false);
    setPendingNavigation(false);
    newHash.current = null;
    window.history.pushState(null, '', window.location.href);
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthLayout>
        <Container maxWidth="md" sx={{ direction: 'ltr' }}>
          <Paper
            elevation={3}
            sx={{ 
              p: 5, 
              borderRadius: 3, 
              textAlign: 'center',
              direction: 'ltr',
              '& *': {
                direction: 'ltr !important'
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <span role="img" aria-label="lock" style={{ fontSize: 28 }}>
                  🔒
                </span>
              </Box>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Set new password
            </Typography>
            <Typography variant="body2" sx={{ color: '#888', mb: 3 }}>
              Must be at least 8 characters.
            </Typography>

            {/* Render AllForm */}
            {SetNewPasswordForm()}

            {/* Display API errors if any */}
            {error && (
              <Typography sx={{ color: 'red', mb: 2 }}>{error}</Typography>
            )}

            <Link
              component="button"
              onClick={handleBackToLogin}
              sx={{
                color: '#888',
                fontSize: 15,
                mt: 1,
                textDecoration: 'none',
                fontFamily:'Livvic'
              }}
            >
              ← Back to log in
            </Link>
            <ConfirmationDialog
              open={showConfirmDialog}
              title="Confirm Navigation"
              message="Are you sure you want to exit? Any unsaved changes will be lost."
              onConfirm={handleConfirmNavigation}
              onCancel={handleCancelNavigation}
            />
          </Paper>
        </Container>
      </AuthLayout>
    </ThemeProvider>
  );
};

export default SetNewPassword;
